import ClockRewind from "@/svg/ClockRewind";
import Plus from "@/svg/Plus";
import { Tags, toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  ClinicalNoteResponse,
  CreateNoteRequest,
} from "../../../api/clinicalTrialsApiTypes";
import { usePatientNotes } from "../../../hooks/usePatientNotes";
import { useEligibilityDetailContext } from "../EligibilityDetailContext";
import { StatusHistoryPanel } from "../shared/StatusHistoryPanel";
import { AddNotePanel } from "./AddNotePanel";
import PatientChat from "./PatientChat";

/** Derive up to two initials from a display name or email local-part. */
function initialsFromLabel(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

/** Full creation timestamp for the notes list. */
function formatNoteTimestamp(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

/** Status pill and left accent for one API note row. */
function notePresentation(note: ClinicalNoteResponse): {
  tag: string;
  leftBorderClass: string;
} {
  if (note.status === "resolved") {
    return {
      tag: "Resolved",
      leftBorderClass: "border-l-tertiaryGreen-500",
    };
  }
  if (note.is_overdue) {
    return { tag: "Overdue", leftBorderClass: "border-l-statusRed-text" };
  }
  return {
    tag: "Open",
    leftBorderClass: note.is_unread
      ? "border-l-tertiaryBlue-4"
      : "border-l-transparent",
  };
}

interface NotecardProps {
  note: ClinicalNoteResponse;
  onOpen: () => void;
}

const Notecard = ({ note, onOpen }: NotecardProps) => {
  const author = note.creator.display_name || note.creator.email || "Unknown";
  const { tag, leftBorderClass } = notePresentation(note);
  return (
    <button
      type="button"
      className={`notecard w-full border-r-0 border-t-0 border-b border-l-[3px] border-primaryGray-14 p-3 h-[4.375rem] bg-white flex gap-4 items-center text-left cursor-pointer hover:bg-primaryGray-16 focus-visible:outline focus-visible:outline-2 focus-visible:outline-tertiaryBlue-4 ${leftBorderClass}`}
      aria-label={`Open note: ${note.title}`}
      onClick={onOpen}
    >
      <div className="note-content flex-1 min-w-0">
        <div className="note-heading text-smaller font-[700] text-primaryGray-1 truncate mb-1">
          {note.title}
        </div>
        <div className="note-metadata flex gap-[0.38rem] items-center justify-start">
          <div className="user-badge flex items-center justify-center h-[1.125rem] w-[1.125rem] bg-[#0056D6] rounded-full text-[0.5rem] text-white">
            {initialsFromLabel(author)}
          </div>
          <div className="user-name text-x-tiny text-primaryGray-3">
            {author}
          </div>
          <div className="icon text-[#d0d0d0]">&#8226;</div>
          <div className="timestamp text-x-tiny text-primaryGray-3">
            {formatNoteTimestamp(note.created_at)}
          </div>
        </div>
      </div>
      <div className="note-tag">
        <Tags
          label={tag}
          backgroundColor={note.status === "resolved" ? "#dcfce7" : undefined}
          textColor={note.status === "resolved" ? "#166534" : undefined}
        />
      </div>
    </button>
  );
};

/**
 * Patient-wide notes sidebar backed by `GET /patients/{mrn}/notes`.
 *
 * @returns Docked notes list for the eligibility details screen
 */
const PatientNotes = () => {
  const { trialId, mrn, refreshDetail } = useEligibilityDetailContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const targetNoteId = searchParams.get("noteId");
  const {
    notes,
    assignees,
    isLoading,
    isSaving,
    error,
    create,
    reply,
    command,
    addParticipants,
    removeParticipant,
    markRead,
  } = usePatientNotes(mrn, targetNoteId ?? undefined);
  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);
  const [isStatusHistoryOpen, setIsStatusHistoryOpen] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const selectedNote = notes.find((note) => note.note_id === selectedNoteId);
  const markedReadNoteId = useRef<string | null>(null);

  useEffect(() => {
    if (
      !selectedNote ||
      selectedNote.current_user_is_participant !== true ||
      markedReadNoteId.current === selectedNote.note_id ||
      selectedNote.current_user_is_unread === false
    ) {
      return;
    }
    markedReadNoteId.current = selectedNote.note_id;
    void markRead(selectedNote.note_id).catch(() => {
      markedReadNoteId.current = null;
    });
  }, [markRead, selectedNote]);

  useEffect(() => {
    if (!targetNoteId || !notes.some((note) => note.note_id === targetNoteId))
      return;
    setSelectedNoteId(targetNoteId);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("noteId");
    nextParams.delete("activityId");
    setSearchParams(nextParams, { replace: true });
  }, [notes, searchParams, setSearchParams, targetNoteId]);

  /**
   * Create a patient note via `POST /patients/{mrn}/notes` and refresh detail counts.
   *
   * @param request - Note title, body, and optional assignee or due date
   * @returns Nothing once the note is persisted
   */
  const saveNote = useCallback(
    async (request: CreateNoteRequest): Promise<void> => {
      await create(request);
      refreshDetail?.();
    },
    [create, refreshDetail],
  );

  const showRecipientError = useCallback((action: "add" | "remove"): void => {
    toast.error(
      action === "add"
        ? "Failed to add recipient."
        : "Failed to remove recipient.",
    );
  }, []);

  if (isAddNoteOpen) {
    return (
      <AddNotePanel
        assignees={assignees}
        isSaving={isSaving}
        onClose={() => setIsAddNoteOpen(false)}
        onSave={saveNote}
      />
    );
  }

  if (selectedNote) {
    return (
      <PatientChat
        key={selectedNote.note_id}
        note={selectedNote}
        assignees={assignees}
        isSaving={isSaving}
        onBack={() => setSelectedNoteId(null)}
        onReply={async (body) => {
          try {
            await reply(selectedNote.note_id, { body });
          } catch (reason) {
            toast.error("Failed to send reply.");
            throw reason;
          }
        }}
        onAddRecipients={async (userIds, expectedVersion) => {
          try {
            await addParticipants(
              selectedNote.note_id,
              userIds,
              expectedVersion,
            );
          } catch {
            showRecipientError("add");
          }
        }}
        onRemoveRecipient={async (userId) => {
          try {
            await removeParticipant(selectedNote.note_id, userId);
          } catch {
            showRecipientError("remove");
          }
        }}
        onResolve={async () => {
          try {
            await command(selectedNote.note_id, "resolve");
            refreshDetail?.();
          } catch (reason) {
            toast.error("Failed to resolve note.");
            throw reason;
          }
        }}
      />
    );
  }

  return (
    <div className="patient-notes--container flex flex-col bg-white overflow-hidden h-full ">
      <div className="header h-[3.75rem] border-b border-primaryGray-14 flex justify-between px-3 py-5">
        <div className="header-text text-sm font-semibold">
          {isStatusHistoryOpen ? "Status History" : "Patient Notes"}
        </div>
        <div className="actions flex gap-3 items-center">
          {!isStatusHistoryOpen ? (
            <button
              type="button"
              className="add-icon hover:scale-105 transition-all cursor-pointer border-0 bg-transparent p-0"
              aria-label="Add note"
              onClick={() => setIsAddNoteOpen(true)}
            >
              <Plus />
            </button>
          ) : null}
          <button
            type="button"
            className={`clock-icon border-0 bg-transparent p-0 transition-all ${
              isStatusHistoryOpen
                ? "rounded-full ring-2 ring-tertiaryBlue-4"
                : "hover:scale-105"
            } cursor-pointer`}
            aria-label={
              isStatusHistoryOpen
                ? "Back to patient notes"
                : "View workflow status history"
            }
            aria-pressed={isStatusHistoryOpen}
            onClick={() => setIsStatusHistoryOpen((open) => !open)}
          >
            <ClockRewind />
          </button>
        </div>
      </div>
      <div className="body flex-1 min-h-0 overflow-auto">
        {isStatusHistoryOpen ? (
          <StatusHistoryPanel
            trialId={trialId}
            mrn={mrn}
            active={isStatusHistoryOpen}
            assignees={assignees}
          />
        ) : isLoading ? (
          <p className="p-4 text-center text-x-tiny text-primaryGray-3">
            Loading notes…
          </p>
        ) : error ? (
          <div className="p-4 text-center">
            <p className="mb-2 text-x-tiny text-statusRed-text">{error}</p>
          </div>
        ) : !notes.length ? (
          <p className="p-4 text-center text-x-tiny text-primaryGray-3">
            No patient notes yet.
          </p>
        ) : (
          notes.map((note) => (
            <Notecard
              key={note.note_id}
              note={note}
              onOpen={() => setSelectedNoteId(note.note_id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default PatientNotes;
